import type { EntitlementKey } from "./entitlements/modules";

export type SystemRole = 'platform_admin' | 'owner' | 'admin' | 'developer' | 'operator' | 'viewer' | 'end_user';

export interface UserContext {
  sub: string;
  email: string;
  role: SystemRole;
  orgId: string;
  endOrgId?: string;
  orgType?: string;
  accountName?: string;
  displayName?: string;
  /**
   * True when `role` came from somewhere authoritative — a `role` claim actually
   * present on the token, or a server response that states it.
   *
   * False/absent means `role` is the least-privilege FALLBACK, not an answer. A
   * brand-new signup's token is minted before provisioning writes the org and
   * role onto the Keycloak user, so it carries neither; `role` still has to hold
   * a SystemRole for every gate that reads it, so it falls back to viewer. Without
   * this flag that guess is indistinguishable from a token that really says
   * viewer, which is what left a brand-new owner in a read-only dashboard: the
   * guess was never revisited once the memberships response arrived with the
   * real answer.
   */
  roleResolved?: boolean;
}

const ROLE_HIERARCHY: Record<SystemRole, number> = {
  platform_admin: 8, owner: 7, admin: 6, developer: 5, operator: 4,
  viewer: 2, end_user: 1,
};

export function hasMinRole(userRole: SystemRole, minRole: SystemRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[minRole];
}

export function getRoleLabel(role: SystemRole): string {
  const labels: Record<SystemRole, string> = {
    platform_admin: 'Platform Admin',
    owner: 'Owner',
    admin: 'Admin',
    developer: 'Developer',
    operator: 'Operator',
    viewer: 'Viewer',
    end_user: 'End User',
  };
  return labels[role] || role;
}

/**
 * Tier colour for a role, as a design-system token.
 *
 * The previous seven hand-picked hues were seven accent colours, which the
 * design system does not have — it ships one accent plus three chart series.
 * Roles are grouped by privilege tier onto those instead:
 *
 *   platform  chart-3   platform_admin
 *   account   chart-1   owner, admin
 *   builder   chart-2   developer
 *   tenant    text-body operator, viewer
 *   embed     text-muted end_user
 *
 * Roles inside a tier share a colour deliberately. Every caller renders
 * `getRoleLabel()` alongside, so the label carries the meaning and colour is
 * reinforcement — which is also the accessibility rule: never colour alone.
 */
export function getRoleColor(role: SystemRole): string {
  const colors: Record<SystemRole, string> = {
    platform_admin: 'var(--fs-chart-3)',
    owner: 'var(--fs-chart-1)',
    admin: 'var(--fs-chart-1)',
    developer: 'var(--fs-chart-2)',
    operator: 'var(--fs-text-body)',
    viewer: 'var(--fs-text-body)',
    end_user: 'var(--fs-text-muted)',
  };
  return colors[role] || 'var(--fs-text-muted)';
}

/** Translucent tint of the role colour, for badge backgrounds. */
export function getRoleTint(role: SystemRole, percent = 12): string {
  return `color-mix(in srgb, ${getRoleColor(role)} ${percent}%, transparent)`;
}

// Nav items visible per role
export interface NavItem {
  path: string;
  label: string;
  /** Rail children. The prototype expands Integrations, Activity and Settings
   *  in place; each child links at the URL its tab strip already uses, so deep
   *  links and the strips keep working. */
  children?: NavItem[];
  minRole?: SystemRole;
  roles?: SystemRole[];
  /** Hide the item unless the org holds this entitlement. Resolved by the
   *  Sidebar; a nav entry that leads somewhere the org cannot use is the same
   *  dead end as a button that 403s. */
  entitlement?: EntitlementKey;
  section?: 'main' | 'settings';
  group?: string;
}

// ── Admin nav (platform_admin only, /admin/* routes) ──

export function getAdminMainNavItems(): NavItem[] {
  return [
    { path: '/admin', label: 'Overview', section: 'main' },
    { path: '/admin/integrations', label: 'Integrations', section: 'main' },
    { path: '/admin/connector-builder', label: 'Connector Builder', section: 'main' },
    { path: '/admin/accounts', label: 'Accounts', section: 'main' },
    { path: '/admin/onboarding-reviews', label: 'Reviews', section: 'main' },
    { path: '/admin/audit', label: 'Audit Log', section: 'main' },
    { path: '/admin/feedback', label: 'Feedback', section: 'main' },
    { path: '/admin/ai-costs', label: 'AI Costs', section: 'main' },
    { path: '/admin/metrics', label: 'Metrics', section: 'main' },
    { path: '/admin/billing', label: 'Billing', section: 'main' },
    { path: '/admin/health', label: 'System Health', section: 'main' },
    { path: '/admin/ai-models', label: 'AI Models', section: 'main' },
    { path: '/admin/unified-catalog', label: 'Unified API', section: 'main' },
    { path: '/admin/entitlements', label: 'Entitlements', section: 'main' },
    { path: '/admin/v1-workspace', label: 'v1 Workspace Bridge', section: 'main' },
    { path: '/admin/oauth-apps', label: 'OAuth apps', section: 'main' },
    { path: '/admin/platform-users', label: 'Platform Users', section: 'main' },
    { path: '/admin/settings', label: 'Settings', section: 'main' },
  ];
}

// ── Primary nav (4 items — Home, Integrations, Activity, Settings) ──

/** Returns the first settings path this role can access */
export function getDefaultSettingsPath(role: SystemRole): string | null {
  const items = getSettingsSidebarItems(role);
  return items.length > 0 ? items[0].path : null;
}

export function getPrimaryNavItems(role: SystemRole): NavItem[] {
  const settingsPath = getDefaultSettingsPath(role);
  const all: NavItem[] = [
    { path: '/', label: 'Home', section: 'main' },
    { path: '/agent', label: 'Agent', roles: ['platform_admin', 'owner', 'admin', 'developer', 'operator'], section: 'main' },
    { path: '/integrations', label: 'Integrations', section: 'main' },
    { path: '/widgets', label: 'Widgets', section: 'main' },
    { path: '/activity', label: 'Activity', roles: ['platform_admin', 'owner', 'admin', 'developer', 'operator', 'viewer'], section: 'main' },
    // Onboarding guides. Mirrors GUIDE_ROLES in hooks/use-guides.ts and the
    // route gate in App.tsx — all three must agree or a nav link leads to a
    // page that is empty or denied. developer+ because every guide in
    // lib/guides.ts is minRole developer; no platform_admin because its prod
    // token carries no org_id (CLAUDE.md rule 19) and guides are account-scoped.
    { path: '/guides', label: 'Guides', roles: ['owner', 'admin', 'developer'], section: 'main' },
  ];
  // Only show Settings if user has at least one accessible settings page
  if (settingsPath) {
    all.push({ path: settingsPath, label: 'Settings', section: 'main' });
  }
  return all.filter(item => {
    if (item.roles) return item.roles.includes(role);
    return true;
  });
}

// ── Sidebar nav (grouped) ──

/**
 * The left rail's grouped navigation, matching the approved sidebar prototype:
 * an ungrouped Home, then Build / Operate / Manage.
 *
 * Two deliberate differences from `getPrimaryNavItems`, both from the
 * prototype:
 *  - Customers is promoted out of Settings to a top-level Operate item. The
 *    route (/settings/customers) and its admin gate are unchanged; only the
 *    entry point moves.
 *  - Agent and Guides are not rail items. Agent is the first tab of
 *    Integrations, where the prototype puts it; Guides is reachable at
 *    /guides and from the "Getting started" card on Home.
 *
 * Role gates are carried over verbatim from getPrimaryNavItems and
 * getSettingsSidebarItems - the rail must never surface a page the role
 * cannot open.
 */
export interface NavGroup {
  /** Section heading. null renders the items with no heading, as Home does. */
  label: string | null;
  items: NavItem[];
}

export function getSidebarNavGroups(role: SystemRole): NavGroup[] {
  const settingsPath = getDefaultSettingsPath(role);

  const groups: NavGroup[] = [
    { label: null, items: [{ path: '/', label: 'Home', section: 'main' }] },
    {
      label: 'Build',
      items: [
        {
          path: '/integrations',
          label: 'Integrations',
          section: 'main',
          children: [
            // Agent owns its own full-height layout, so it stays at /agent.
            {
              path: '/agent',
              label: 'Agent',
              roles: ['platform_admin', 'owner', 'admin', 'developer', 'operator'],
              entitlement: 'aiBuilder.integrations',
            },
            { path: '/integrations?tab=connectors', label: 'Connectors' },
            { path: '/integrations?tab=unified', label: 'Unified APIs' },
            { path: '/integrations?tab=connections', label: 'Connections' },
            {
              path: '/integrations?tab=workflows',
              label: 'Workflows',
              roles: ['platform_admin', 'owner', 'admin', 'developer', 'operator', 'viewer'],
            },
            {
              path: '/integrations?tab=triggers',
              label: 'Triggers',
              roles: ['platform_admin', 'owner', 'admin', 'developer', 'operator', 'viewer'],
            },
            // The tenant-facing half of the two-stage update flow: a patch to
            // THIS org's own workflow, with the diff, so it can be refused.
            // That is the decision a tenant actually owns — stage 1, deciding
            // what to do about the vendor's change to a managed connector, is
            // taken once by the team that owns the connector, in the admin
            // console.
            //
            // It had NO sidebar entry at all until now. Its only route in was a
            // deep link from the Review updates page, on a button that appears
            // only once a fix has been applied AND fanned out to this org — so
            // the page holding a tenant's own decision was reachable only
            // through a page about someone else's, and undiscoverable before
            // that. Hence this entry.
            //
            // Roles match the ones Review updates carried: those that can ACT.
            // Approving a patch to your workflow is that same kind of act.
            {
              path: '/integrations/updates',
              label: 'Pending updates',
              roles: ['platform_admin', 'owner', 'admin', 'developer'],
            },
            // Review updates (/integrations/review-updates) is deliberately NOT
            // listed. The page, its route and every redirect into it still
            // work — this hides it from the sidebar only, because stage 1 is a
            // platform decision and the tenant sidebar should point at the
            // tenant's own. Re-add an entry here to bring it back.
          ],
        },
        {
          path: '/widgets',
          label: 'Widgets',
          roles: ['platform_admin', 'owner', 'admin', 'developer', 'operator', 'viewer'],
          section: 'main',
        },
      ],
    },
    {
      label: 'Operate',
      items: [
        {
          path: '/activity',
          label: 'Activity',
          roles: ['platform_admin', 'owner', 'admin', 'developer', 'operator', 'viewer'],
          section: 'main',
          children: [
            { path: '/activity/events', label: 'Events' },
            { path: '/activity/traces', label: 'Traces' },
            { path: '/activity/alerts', label: 'Alerts' },
            { path: '/activity/executions', label: 'Executions' },
            { path: '/activity/sync-reports', label: 'Sync Reports' },
          ],
        },
        // Same route and gate as the Settings entry it is promoted from.
        { path: '/settings/customers', label: 'Customers', minRole: 'admin', section: 'main' },
        // The cross-customer rollup of what the widget's Insights panel shows one customer at a
        // time. `developer` and above, which is exactly the set of account-tier roles the API's
        // own gate admits — a tenant-tier role has no customers of its own to report on, and the
        // endpoint refuses it, so an entry here would be a dead end.
        { path: '/reporting', label: 'Reporting', minRole: 'developer', section: 'main' },
      ],
    },
  ];

  // Settings resolves to the first page this role can actually open, so the
  // rail never links a role into a denied page.
  if (settingsPath) {
    groups.push({
      label: 'Manage',
      items: [{
        path: settingsPath,
        label: 'Settings',
        section: 'main',
        // The authoritative settings list, previously owned by the (now
        // removed) SettingsSidebar component — getSettingsSidebarItems covers
        // only a subset, which is why the rail was short six entries.
        // Customers is absent here: it is promoted to Operate.
        children: ([
          { path: '/settings/people', label: 'People', minRole: 'admin' },
          { path: '/settings/general', label: 'General', minRole: 'admin' },
          { path: '/settings/api-keys', label: 'API keys', minRole: 'developer' },
          { path: '/settings/mcp', label: 'MCP server', minRole: 'developer' },
          { path: '/settings/secrets', label: 'Secrets', minRole: 'developer' },
          { path: '/settings/environments', label: 'Environments', minRole: 'developer' },
          { path: '/settings/configs', label: 'Configs', minRole: 'developer' },
          { path: '/settings/database', label: 'Database', minRole: 'developer' },
          { path: '/settings/saas-connectors', label: 'SaaS Connectors', minRole: 'admin' },
          { path: '/settings/billing', label: 'Billing', minRole: 'admin' },
          // Roles, then Audit log, then Trash — the design's order. Roles was
          // last, which put it after the destructive entry and split it from
          // People, the other permissions page.
          { path: '/settings/roles', label: 'Roles', minRole: 'admin' },
          { path: '/settings/audit', label: 'Audit log', minRole: 'admin' },
          { path: '/settings/trash', label: 'Trash', minRole: 'developer' },
        ] as NavItem[]).filter((item) => canSeeNavItem(item, role)),
      }],
    });
  }

  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => canSeeNavItem(item, role)) }))
    .filter((group) => group.items.length > 0);
}

function canSeeNavItem(item: NavItem, role: SystemRole): boolean {
  if (item.roles) return item.roles.includes(role);
  if (item.minRole) return hasMinRole(role, item.minRole);
  return true;
}

// ── Settings sidebar items ──

export interface SettingsSidebarItem {
  path: string;
  label: string;
  minRole?: SystemRole;
  group?: 'main' | 'advanced';
}

export function getSettingsSidebarItems(role: SystemRole): SettingsSidebarItem[] {
  const all: SettingsSidebarItem[] = [
    { path: '/settings/people', label: 'People', minRole: 'admin' },
    { path: '/settings/general', label: 'General', minRole: 'admin' },
    { path: '/settings/api-keys', label: 'API keys', minRole: 'developer' },
    { path: '/settings/mcp', label: 'MCP server', minRole: 'developer' },
    { path: '/settings/billing', label: 'Billing', minRole: 'admin' },
    { path: '/settings/customers', label: 'Customers', minRole: 'admin' },
    { path: '/settings/audit', label: 'Audit log', minRole: 'admin' },
    // Advanced (admin+ only)
    { path: '/settings/roles', label: 'Roles', minRole: 'admin', group: 'advanced' },
  ];
  return all.filter(item => {
    if (item.minRole) return hasMinRole(role, item.minRole);
    return true;
  });
}

// getMainNavItems and getSettingsNavItems are kept for mobile menu and backward compat
export function getMainNavItems(role: SystemRole): NavItem[] {
  return getPrimaryNavItems(role);
}

export function getSettingsNavItems(role: SystemRole): NavItem[] {
  // Simplified: returns settings items for the mobile menu only
  const items: NavItem[] = [
    { path: '/settings/people', label: 'People', roles: ['platform_admin', 'owner', 'admin'], section: 'settings' },
    { path: '/settings/general', label: 'General', roles: ['platform_admin', 'owner', 'admin'], section: 'settings' },
    { path: '/settings/api-keys', label: 'API keys', roles: ['platform_admin', 'owner', 'admin', 'developer'], section: 'settings' },
    { path: '/settings/billing', label: 'Billing', roles: ['platform_admin', 'owner', 'admin'], section: 'settings' },
  ];
  return items.filter(item => {
    if (item.roles) return item.roles.includes(role);
    return true;
  });
}
